import { useEffect, useState } from "react";
import { useAuth  }  from "../../contexts/UserContext";

// Hooks
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { useMetaDescription } from "../../hooks/useMetaDescription";
import { Paginate }  from "../Proposals/styles";
import { Oval     }  from "react-loader-spinner";
import { comum    }  from "../../styles/Themes/light";
import ReactPaginate from "react-paginate";
import toast from "react-hot-toast";
import { toastDefs, toastDefsError } from '../../config/toastConfig';

import ButtonIcon   from "../../components/form/ButtonIcon";
import api from "../../services/api";
import { Mails } from "lucide-react";

import { IconAdvancePagination, IconBackPagination, IconListProps, IconProfileButtonDown, IconProfileButtonUp } from "../../components/icons";
import { 
    Container, 
    Menu,
    TableContainer, 
    ApplicantCard, 
    ApplicantWrapper, 
    ApplicantTop, 
    ApplicantBottom,
    DivIcon,
    DivName,
    DivDocument,
    DivContact,
    DivCode,
    DivStatus,
    DivArrow,
    StatusBadge,
    DivActionsTop,
    ReenviarIconButton 
} from "./styles";
import ModalCriaEscritorioSolicitante from "../../components/EscritorioSolicitante/ModalCriaEscritorioSolicitante";
import ApplicantsTab from "../../components/Ui/ApplicantsTabs";
import ApplicantsFilter from "../../components/Ui/ApplicantsFilter";

const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
};

const formatPhone = (phone: string) => {
    // Formata como (11) 99999-9999
    if (phone.length === 11) {
        return phone.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    // Formata como (11) 9999-9999 para números com 10 dígitos
    if (phone.length === 10) {
        return phone.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
    return phone;
};

const formatCpfCnpj = (document: string) => {
    if (!document) return '000.000.000-00';
    
    // Remove qualquer caractere que não seja número
    const cleanDocument = document.replace(/\D/g, '');
    
    // Se tem 11 dígitos, é CPF
    if (cleanDocument.length === 11) {
        return cleanDocument.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    
    // Se tem 14 dígitos, é CNPJ
    if (cleanDocument.length === 14) {
        return cleanDocument.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }
    
    // Se não tem o tamanho esperado, retorna o valor original ou um placeholder
    return document || '000.000.000-00';
};

const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
        case 'ativo':
            return 'success';
        case 'pendente':
            return 'warning';
        default:
            return 'default';
    }
};

export default function Applicants() {
    // Define o título e meta description da página
    useDocumentTitle({ title: "Solicitantes" });
    useMetaDescription({ 
        description: "Gerencie solicitantes na EasyJur Legal Ops. Visualize, filtre e acompanhe o perfil dos solicitantes do sistema." 
    });

    const { setPageName, setPageType } = useAuth();
    const [ loading, setLoading ] = useState<boolean>(false);
    const [ openModalNewApplicant, setOpenModalNewApplicant ] = useState<boolean>(false);
    const [ currentPage, setCurrentPage ] = useState<number>(0);
    const [ itemsPerPage ] = useState<number>(10);
    const [ applicants, setApplicants ] = useState<any[]>([]);
    const [ totalItems, setTotalItems ] = useState<number>(0);
    const [ expandedRows, setExpandedRows ] = useState<Set<string>>(new Set());
    const [ selectedTab, setSelectedTab ] = useState<string>('todos');
    const [ searchTerm, setSearchTerm ] = useState<string>('');
    const [ reenviandoIds, setReenviandoIds ] = useState<Set<number>>(new Set());
    
    const applicantsFilteredBySearch = applicants.filter(applicant => {
        return !searchTerm || applicant.nome?.toLowerCase().includes(searchTerm.toLowerCase());
    });
    
    const todosCount = applicantsFilteredBySearch.length;
    const ativosCount = applicantsFilteredBySearch.filter(applicant => applicant.status?.toLowerCase() === 'ativo').length;
    const pendentesCount = applicantsFilteredBySearch.filter(applicant => applicant.status?.toLowerCase() === 'pendente').length;
    
    const filteredApplicants = applicantsFilteredBySearch.filter(applicant => {
        const matchesTab = selectedTab === 'todos' 
            || (selectedTab === 'ativos' && applicant.status?.toLowerCase() === 'ativo')
            || (selectedTab === 'pendentes' && applicant.status?.toLowerCase() === 'pendente');
        
        return matchesTab;
    });
    
    const totalFilteredItems = filteredApplicants.length;
    const totalPages = Math.ceil(totalFilteredItems / itemsPerPage);
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentApplicants = filteredApplicants.slice(startIndex, endIndex);

    // Função para buscar os dados do endpoint "/escritorio"
    async function fetchApplicants() {
        try {
            setLoading(true);

            const response = await api.get('/escritorio');

            console.log('response.data :>> ', response.data);
            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Erro ao buscar solicitantes');
            }

            setApplicants(response.data.result || []);
            setTotalItems(response.data.result?.length || 0);
            setLoading(false);
            console.log('response.data.result :>> ', response.data.result);

        } catch (error: any) {
            console.log('Erro ao buscar solicitantes:', error);
            
            toast.error(error.message || 'Erro ao carregar solicitantes', toastDefsError);
            
            setLoading(false);
        }
    }

    // Função para lidar com mudança de página
    const handlePageChange = (selectedItem: { selected: number }) => {
        setCurrentPage(selectedItem.selected);
    };

    useEffect(() => {
        setCurrentPage(0);
    }, [selectedTab, searchTerm]);

    async function handleReenviarConvite(id_escritorio_solicitante: number) {
        if (reenviandoIds.has(id_escritorio_solicitante)) {
            return;
        }

        try {
            setReenviandoIds(prev => new Set(prev).add(id_escritorio_solicitante));
            const response = await api.post(`/escritorio/${id_escritorio_solicitante}/reenviar-convite`);

            if (response.data.status !== 'success') {
                throw new Error(response.data.message || 'Erro ao reenviar convite');
            }

            toast.success('Convite reenviado com sucesso', toastDefs);
        } catch (error: any) {
            toast.error(error.response?.data?.message || 'Erro ao reenviar convite', toastDefsError);
        } finally {
            setReenviandoIds(prev => {
                const next = new Set(prev);
                next.delete(id_escritorio_solicitante);
                return next;
            });
        }
    }

    // Função para alternar o estado do collapse
    const toggleExpanded = (applicantId: string) => {
        const newExpandedRows = new Set(expandedRows);
        if (expandedRows.has(applicantId)) {
            newExpandedRows.delete(applicantId);
        } else {
            newExpandedRows.add(applicantId);
        }
        setExpandedRows(newExpandedRows);
    };

    useEffect(() => {
        setPageName('Solicitantes');
        setPageType('solicitantes');
        fetchApplicants();
    }, []);

    return (
        <Container>
            <ButtonIcon
                Icon={<IconListProps />}
                children={'Novo Solicitante'}
                styles="op"
                onClick={() => setOpenModalNewApplicant(true)}
            />
            
            <Menu>
                <ApplicantsTab
                    selected={selectedTab}
                    setSelected={setSelectedTab}
                    todos={todosCount}
                    ativos={ativosCount}
                    pendentes={pendentesCount}
                />
                <ApplicantsFilter
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                />
            </Menu>
            
            {
                loading &&
                <Oval
                    height={150}
                    width={150}
                    color={comum.colors.primaryRed400}
                    wrapperStyle={{}}
                    wrapperClass="loader"
                    visible={true}
                    ariaLabel='oval-loading'
                    secondaryColor={comum.colors.primaryRed400}
                    strokeWidth={2}
                    strokeWidthSecondary={2}
                />
            }
            {
                !loading &&
                <TableContainer>                
                    {currentApplicants.map((applicant, index) => {
                        const uniqueId = applicant.id || `applicant-${index}`;
                        const idEscritorio = applicant.id_escritorio_solicitante || applicant.id;
                        const statusPendente = (applicant.status || '').toLowerCase() === 'pendente';
                        const podeReenviar = statusPendente && idEscritorio;
                        return (
                        <ApplicantCard key={uniqueId}>
                            <ApplicantWrapper menuOpen={expandedRows.has(uniqueId)}>
                                <ApplicantTop>
                                    <DivName>
                                        <div className="title">Nome:</div>
                                        <div className="subtitle">{applicant.nome?.length > 45 ? `${applicant.nome.substring(0, 45)}...` : applicant.nome}</div>
                                        {applicant.nome?.length > 45 && (
                                            <div className="show-name">{applicant.nome}</div>
                                        )}
                                    </DivName>

                                    <DivDocument>
                                        <div className="title">CPF/CNPJ:</div>
                                        <div className="subtitle">{formatCpfCnpj(applicant.cpfCnpj)}</div>
                                    </DivDocument>

                                    <DivContact>
                                        <div className="title">Contato:</div>
                                        <div className="subtitle">{applicant.email}</div>
                                        <div className="subtitle">{formatPhone(applicant?.telefone || '')}</div>
                                    </DivContact>


                                    <DivCode>
                                        <div className="title">Código Interno:</div>
                                        <div className="subtitle">{applicant.codigo ? `C${applicant.codigo}` : 'Não possui'}</div>
                                    </DivCode>

                                    <DivCode>
                                        <div className="title">Código:</div>
                                        <div className="subtitle">{applicant.id_escritorio_solicitante? `ES${applicant.id_escritorio_solicitante}` : 'Não possui'}</div>
                                    </DivCode>

                                    <DivStatus>
                                        <div className="title">Status:</div>
                                        <StatusBadge className={getStatusColor(applicant.status)}>
                                            {applicant.status}
                                        </StatusBadge>
                                    </DivStatus>

                                    <DivActionsTop>
                                        {podeReenviar && (
                                            <ReenviarIconButton
                                                type="button"
                                                disabled={reenviandoIds.has(idEscritorio)}
                                                onClick={() => handleReenviarConvite(idEscritorio)}
                                                title="Reenviar convite"
                                            >
                                                <Mails size={18} />
                                            </ReenviarIconButton>
                                        )}
                                    </DivActionsTop>

                                    <DivArrow onClick={() => toggleExpanded(uniqueId)}>
                                        {expandedRows.has(uniqueId) 
                                            ? <IconProfileButtonUp />
                                            : <IconProfileButtonDown />
                                        }
                                    </DivArrow>
                                </ApplicantTop>
                            
                                <ApplicantBottom menuOpen={expandedRows.has(uniqueId)}>
                                    <DivIcon>
                                    </DivIcon>

                                    <DivDocument>
                                        <div className="title">Tipo:</div>
                                        <div className="subtitle">{applicant.cpfCnpj?.replace(/\D/g, '').length === 14 ? 'Pessoa Jurídica' : 'Pessoa Física'}</div>
                                    </DivDocument>

                                    <DivDocument>
                                        <div className="title">Data Registro:</div>
                                        <div className="subtitle">{formatDate(applicant.criado)}</div>
                                    </DivDocument>

                                </ApplicantBottom>
                            </ApplicantWrapper>
                        </ApplicantCard>
                        );
                    })}
                </TableContainer>
            }

            {
                totalPages > 1 && (
                    <Paginate>
                        <ReactPaginate
                            breakLabel="..."
                            nextLabel={<IconAdvancePagination color={comum.colors.interfaceTextGray}/>}
                            previousLabel={<IconBackPagination color={comum.colors.interfaceTextGray}/>}
                            onPageChange={handlePageChange}
                            pageRangeDisplayed={1}
                            marginPagesDisplayed={1}
                            pageCount={totalPages}
                            forcePage={currentPage}
                            pageClassName="page-item"
                            pageLinkClassName="page-link"
                            previousClassName="back-page"
                            previousLinkClassName="back-link-page"
                            nextClassName="next-page"
                            nextLinkClassName="next-link-page"
                            breakClassName="page-item"
                            breakLinkClassName="page-link"
                            containerClassName="pagination"
                            activeClassName="active"
                            activeLinkClassName="active"
                        />
                    </Paginate>
                )
            }

            <ModalCriaEscritorioSolicitante
                openModalNewApplicant={openModalNewApplicant}
                setOpenModalNewApplicant={setOpenModalNewApplicant}
                onSuccess={fetchApplicants}
            />

        </Container>
    )
}